import { z } from 'zod'

/**
 * Helper functions for common Zod superRefine validation patterns
 */
export const zodValidationHelpers = {
  /**
   * Add required field validation for integers (matches z.number().min(1, 'Field is required'))
   */
  requiredNumber: (ctx: z.RefinementCtx, path: string[], message = 'Field is required') => {
    ctx.addIssue({
      code: z.ZodIssueCode.too_small,
      minimum: 1,
      type: 'number',
      inclusive: true,
      message,
      path,
    })
  },

  /**
   * Add required field validation for decimals (matches z.number().positive('Field must be greater than zero'))
   */
  requiredDecimal: (ctx: z.RefinementCtx, path: string[], message = 'Field must be greater than zero') => {
    ctx.addIssue({
      code: z.ZodIssueCode.too_small,
      minimum: 0,
      type: 'number',
      inclusive: false,
      message,
      path,
    })
  },

  /**
   * Add required field validation for strings (matches z.string().min(1, 'Field is required'))
   */
  requiredString: (ctx: z.RefinementCtx, path: string[], message = 'Field is required') => {
    ctx.addIssue({
      code: z.ZodIssueCode.too_small,
      minimum: 1,
      type: 'string',
      inclusive: true,
      message,
      path,
    })
  },

  /**
   * Add required field validation for dates (matches z.date() for null/undefined values)
   */
  requiredDate: (ctx: z.RefinementCtx, path: string[], message = 'Field is required') => {
    ctx.addIssue({
      code: z.ZodIssueCode.invalid_type,
      expected: 'date',
      received: 'null',
      message,
      path,
    })
  },

  /**
   * Add minimum value validation for numbers (matches z.number().min())
   */
  minNumber: (ctx: z.RefinementCtx, path: string[], minimum: number, message?: string) => {
    ctx.addIssue({
      code: z.ZodIssueCode.too_small,
      minimum,
      type: 'number',
      inclusive: true,
      message: message || `Field must be at least ${minimum}`,
      path,
    })
  },

  /**
   * Validate decimal that is required when flag is true, optional but must be positive when flag is false
   */
  conditionalRequiredDecimal: (
    ctx: z.RefinementCtx,
    path: string[],
    value: number | null,
    isRequired: boolean,
    message = 'Amount must be greater than zero'
  ) => {
    const isInvalid = isRequired
      ? !value || value <= 0
      : value !== null && value <= 0

    if (isInvalid) {
      zodValidationHelpers.requiredDecimal(ctx, path, message)
    }
  },
} 